import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { Banner, BusinessName, ImageWrapper, ManageAppointmentLink, OwnerName } from "./styles";
import KLButton from "../../components/KLButton";
import KLSignInModal from "../../components/BookingAuth/KLSignInModal";
import {
  checkCustomerSlots,
  fetchBusiness,
} from "../../store/duckers/customers/thunk";
import {
  Container,
  IconContainer,
  InfoText,
  Subtitle,
  TextIconContainer,
  Title,
} from "../styles";
import KLPleaseWait from "../../components/KLPleaseWait";
import useLocalStorage from "../../hooks/useLocalStorage";
import { StorageKey, StorageKeys } from "../../constants/localStorage";
import { useStep } from "../../context/StepContext";
import { categoryConfigMap } from "../../constants/customers/categoryConfigMap";
import useBusinessImage from "../../hooks/useBusinessImage";
import KLEventAvailableIcon from "../../components/icons/KLEventAvailableIcon";
import { CustomerRouteStep, FlowType } from "../../constants/routes";
import { verifyToken } from "../../store/duckers/tokens/thunk";

const CustomerMainScreen: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { markStepAsCompleted, resetSteps } = useStep();
  const { businessId: urlBusinessId } = useParams<{ businessId: string }>();

  const { business, fetchingBusiness, error } = useSelector(
    (state: RootState) => state.customers,
  );

  const {
    userData: tokenPayload,
    isTokenVerified,
    verifyingToken,
  } = useSelector((state: RootState) => state.tokens);

  const {
    customerSlots,
  } = useSelector((state: RootState) => state.customers);

  const { storedValue: storedBusinessId, setValue: setBusinessIdStorage } =
    useLocalStorage<StorageKey | null>(StorageKeys.BusinessId, null);

  const { storedValue: storedToken } = useLocalStorage<StorageKey | null>(
    StorageKeys.VerifiedCustomerOtpToken,
    null,
  );

  const [businessId, setBusinessId] = useState<string | null>(
    urlBusinessId || storedBusinessId,
  );

  const [signInOpen, setSignInOpen] = useState<boolean>(false);

  useEffect(() => {
    if (urlBusinessId) {
      setBusinessIdStorage(urlBusinessId as StorageKey);
      setBusinessId(urlBusinessId);
    } else if (storedBusinessId) {
      setBusinessId(storedBusinessId);
    }
  }, [urlBusinessId, storedBusinessId, setBusinessIdStorage]);

  useEffect(() => {
    resetSteps(FlowType.Customer);
    // OME-209: Suppressing to preserve existing behavior; see ticket for rationale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Background-verify stored token on mount (not gated by user click)
  useEffect(() => {
    if (!businessId) return;
    if (!storedToken) return;
    if (isTokenVerified || verifyingToken) return;
    dispatch(verifyToken(storedToken));
  }, [dispatch, businessId, storedToken, isTokenVerified, verifyingToken]);

  // Background-fetch customer slots once token is verified
  useEffect(() => {
    if (isTokenVerified && tokenPayload?.phoneNumber && businessId) {
      dispatch(checkCustomerSlots({ businessId, phone: tokenPayload.phoneNumber }));
    }
  }, [isTokenVerified, tokenPayload?.phoneNumber, businessId, dispatch]);

  const handleLetsGo = () => {
    if (businessId) {
      markStepAsCompleted(FlowType.Customer, CustomerRouteStep.Services);
      navigate(`/${businessId}/services`);
    } else {
      navigate("/");
    }
  };

  const handleSignInSuccess = () => {
    setSignInOpen(false);
    if (businessId) {
      navigate(`/${businessId}/existing-slots`);
    }
  };

  useEffect(() => {
    if (!businessId) {
      return;
    }
    dispatch(fetchBusiness(businessId));
  }, [dispatch, businessId]);

  const categoryConfig = business?.category
    ? categoryConfigMap[business.category as keyof typeof categoryConfigMap]
    : undefined;

  const term = categoryConfig?.term || "תור";

  const imageSrc = useBusinessImage({ businessId, categoryConfig });

  if (!businessId) {
    return <Navigate to="/not-authorized" replace />;
  }

  return (
    <Container>
      {fetchingBusiness ? (
        <KLPleaseWait text="בודקים עם מי יש לנו עסק" />
      ) : error ? (
        <>
          <Title>אופס!</Title>
          <InfoText>זה קצת עסק ביש. אנא נסו שוב.</InfoText>
        </>
      ) : business ? (
        <>
          {imageSrc && (
            <ImageWrapper>
              <img src={imageSrc} alt={business.category || "default"} />
            </ImageWrapper>
          )}
          <BusinessName>{business.businessName}</BusinessName>
          <OwnerName>{business.ownerName}</OwnerName>
          <TextIconContainer>
            <IconContainer>
              <KLEventAvailableIcon size="medium" />
            </IconContainer>
            <Subtitle $noMarginBottom>קבעו {term} באישור מיידי!</Subtitle>
          </TextIconContainer>
          <InfoText>
            עשו זאת בקלות - בחרו את סוג השירות שאתם רוצים, ואת היום והשעה
            שמתאימים לכם, וזהו!
          </InfoText>
          {customerSlots.length > 0 && (
            <Banner onClick={() => navigate(`/${businessId}/existing-slots`)}>
              יש לך תור עתידי — לחץ לניהול
            </Banner>
          )}
          <KLButton
            title={"בואו נתחיל"}
            onClick={handleLetsGo}
          />
          {/* Entry point for customers who already have an appointment and
              just want to view/manage it (e.g. incognito visitors with no
              stored session). Hidden when the banner is already showing —
              the banner already gets them there directly. */}
          {customerSlots.length === 0 && (
            <ManageAppointmentLink
              type="button"
              onClick={() => setSignInOpen(true)}
            >
              יש לך כבר תור? כניסה עם קוד
            </ManageAppointmentLink>
          )}
        </>
      ) : null}
      <KLSignInModal
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        onSuccess={handleSignInSuccess}
      />
    </Container>
  );
};

export default CustomerMainScreen;
